import { DatePipe, DecimalPipe } from '@angular/common';
import { isoDateNoPuncts } from '@gestor-web/@core/utils';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { AgGridAngular } from 'ag-grid-angular';
import {
  CellClassParams,
  ColDef,
  ExcelExportParams,
  GridApi,
  GridOptions,
  INumberCellEditorParams,
  ProcessCellForExportParams,
  ToolPanelDef,
  ValueParserParams,
} from 'ag-grid-enterprise';
import { AG_GRID_LOCALE_PT_BR, AgFilterUtils, AgViewsToolpanelComponent, AgViewsToolpanelParams } from '..';

const DEFAULT_GROUP_COL_WIDTH = 128;

const EXPORT_MAX_SIZE = 1_000_000;

const EXPORT_COPYRIGHT: ExcelExportParams = {
  author: 'Gestor Web - Blendus',
  sheetName: 'gestor-web',
};

export type COLUMN_TYPES = 'int' | 'money' | 'float1' | 'float4';

export type PIPES = {
  date?: DatePipe;
  decimal?: DecimalPipe;
  competencia?: CompetenciaPipe;
  cpfCnpj?: CpfCnpjPipe;
};

export type AGG_FUNC = 'sum' | 'min' | 'max' | 'count' | 'avg' | 'first' | 'last';

// Ref.: https://ag-grid.zendesk.com/hc/en-us/articles/360016033891-Styling-Pivot-totals
export function isPivotTotalCell(colDef: ColDef): boolean {
  return colDef.pivotTotalColumnIds && colDef.pivotTotalColumnIds.length > 0 && !colDef.pivotKeys.length;
}

export function isFooterTotalCell(params: CellClassParams): boolean {
  // console.log('isFooterTotalCell', params.node.footer, params);
  return params.node.footer;
}

/**
 * Constrói as definições padrão das colunas do AG Grid.
 *
 * @param overrides sobrescreve configurações específicas de padrão das colunas.
 * @returns definição da coluna padrão.
 */
export function buildDefaultColumnDef<TData = any>(overrides?: ColDef<TData>): ColDef<TData> {
  const colDefault: ColDef<TData> = {
    initialWidth: 150,
    filter: true,
    sortable: true,
    resizable: true,
    enableValue: true,
    enablePivot: true,
    enableRowGroup: true,
    cellClassRules: {},
    ...overrides,
  };
  // Reinforce total styling, after overrides
  colDefault.cellClassRules['pivot-total-col'] = params => isPivotTotalCell(params.colDef);
  colDefault.cellClassRules['footer-total-col'] = params => isFooterTotalCell(params);
  return colDefault;
}

/**
 * Constrói as definições padrão das colunas automáticas de grupos de linhas.
 *
 * @param overrides sobrescreve configurações específicas da coluna.
 * @returns definição da coluna automática de grupo.
 */
export function buildAutoGroupColumnDef<TData = any>(overrides?: ColDef<TData>): ColDef<TData> {
  return {
    initialWidth: DEFAULT_GROUP_COL_WIDTH,
    sortable: true,
    sort: 'asc',
    wrapText: true,
    autoHeight: true,
    filter: 'agGroupColumnFilter',
    cellRendererParams: {
      suppressCount: true, // ex: Group Name ([count]) -> Group Name
    },
    // A coluna de grupo guarda a chave do agrupamento, que e sempre categorica. Sem isso
    // o ag-grid tenta inferir o tipo pela primeira linha ao montar um grafico e avisa
    // `warning #265` quando ela vem nula — um aviso por coluna agrupada, a cada grafico.
    chartDataType: 'category',
    ...overrides,
  } as ColDef<TData>;
}

/**
 * Constrói as definições dos tipos de colunas padrão, conforme  as pipes declaradas.
 *
 * @param pipes objeto de configurações de pipes, caso tenha.
 * @returns tipos de colunas padrão.
 */
export function buildColumnTypes<TData>(pipes?: PIPES): { [key: string]: ColDef<TData> } {
  // Registra todos os tipos sempre: sem a pipe correspondente o formatter degrada para o valor
  // cru, entao nenhuma tela perde o tipo por esquecer de passa-la. Ver ag-grid.factory.spec.ts.
  const columnTypes: { [key: string]: ColDef<TData> } = {
    str: {
      cellClass: 'str',
    },
  };
  Object.assign(columnTypes, {
    int: {
      cellClass: 'int',
      cellStyle: { 'text-align': 'right' },
      enableValue: true,
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      valueFormatter: params => pipes?.decimal ? pipes.decimal.transform(params.value, '1.0-0') : params.value,
      // valueParser: params => parseNumber(params),
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 0,
      } as INumberCellEditorParams,
    },
    money: {
      cellClass: 'money',
      cellStyle: { 'text-align': 'right' },
      enableValue: true,
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      valueFormatter: params => pipes?.decimal ? pipes.decimal.transform(params.value, '1.2-2') : params.value,
      // valueParser: params => parseNumber(params),
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 2,
      } as INumberCellEditorParams,
    },
    float: {
      cellClass: 'float',
      cellStyle: { 'text-align': 'right' },
      enableValue: true,
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      valueFormatter: params => pipes?.decimal ? pipes.decimal.transform(params.value, '1.2-12') : params.value,
      // valueParser: params => parseNumber(params),
      cellEditor: 'agNumberCellEditor',
    },
    float1: {
      cellClass: 'float1',
      cellStyle: { 'text-align': 'right' },
      enableValue: true,
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      valueFormatter: params => pipes?.decimal ? pipes.decimal.transform(params.value, '1.1-1') : params.value,
      // valueParser: params => parseNumber(params),
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 1,
      } as INumberCellEditorParams,
    },
    float4: {
      cellClass: 'float4',
      cellStyle: { 'text-align': 'right' },
      enableValue: true,
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      valueFormatter: params => pipes?.decimal ? pipes.decimal.transform(params.value, '1.4-4') : params.value,
      // valueParser: params => parseNumber(params),
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 4,
      } as INumberCellEditorParams,
    },
  });
  Object.assign(columnTypes, {
    date: {
      cellClass: 'date',
      valueFormatter: params => (!params.value ? '' : pipes?.date ? pipes.date.transform(params.value, 'shortDate') : params.value),
      filterValueGetter: params => parseDate(params.getValue(params.colDef.field), true),
      getQuickFilterText: params => (!params.value ? null : pipes?.date ? pipes.date.transform(params.value, 'shortDate') : params.value),
      cellDataType: 'dateString',
      filter: 'agDateColumnFilter',
      // valueParser: params => parseDate(params.newValue, true),
    },
    datetime: {
      cellClass: 'datetime',
      valueFormatter: params => (!params.value ? '' : pipes?.date ? pipes.date.transform(params.value, 'short') : params.value),
      // filterValueGetter: params => parseDate(params.getValue(params.colDef.field)),
      getQuickFilterText: params => (!params.value ? null : pipes?.date ? pipes.date.transform(params.value, 'short') : params.value),
      cellDataType: 'dateTimeString',
      /* // A partir da versão 34
      filter: 'agDateColumnFilter',
      filterParams: {
        includeTime: true,
      },
      */
      // valueParser: params => parseDate(params.newValue),
    },
  });
  Object.assign(columnTypes, {
    competencia: {
      cellClass: 'competencia',
      valueFormatter: params => pipes?.competencia ? pipes.competencia.transform(params.value) : params.value,
      getQuickFilterText: params => pipes?.competencia ? pipes.competencia.transform(params.value) : params.value,
    },
  });
  Object.assign(columnTypes, {
    cpfCnpj: {
      cellClass: 'cpfCnpj',
      valueFormatter: params => pipes?.cpfCnpj ? pipes.cpfCnpj.transform(params.value) : params.value,
      getQuickFilterText: params => pipes?.cpfCnpj ? pipes.cpfCnpj.transform(params.value) : params.value,
    },
    cpf: {
      cellClass: 'cpf',
      valueFormatter: params => pipes?.cpfCnpj ? pipes.cpfCnpj.transform(params.value) : params.value,
      getQuickFilterText: params => pipes?.cpfCnpj ? pipes.cpfCnpj.transform(params.value) : params.value,
    },
    cnpj: {
      cellClass: 'cnpj',
      valueFormatter: params => pipes?.cpfCnpj ? pipes.cpfCnpj.transform(params.value) : params.value,
      getQuickFilterText: params => pipes?.cpfCnpj ? pipes.cpfCnpj.transform(params.value) : params.value,
    },
  });
  return columnTypes;
}

/**
 * Parâmetros básicos do construtor de configurações do AG Grid.
 */
export interface BuildConfigOptions<TData = any>
  extends Pick<GridOptions<TData>, 'gridId' | 'columnDefs' | 'pivotMode'> {
  pipes?: PIPES;
  exportExcelFn?: () => void;
  views?: AgViewsToolpanelParams;
}

/**
 * Construtor das configurações do AG Grid.
 *
 * Centraliza e compartilha as configurações padrão entre as telas
 * que utilizam o AG Grid.
 *
 * @param buildOptions parâmetros de configurações básicas
 * @param overrides sobrescreve configurações específicas do AG Grid.
 * @returns objeto de configurações do AG Grid.
 */
export function buildGridOptions<TData = any>(
  buildOptions: BuildConfigOptions<TData>,
  overrides?: GridOptions<TData>,
): GridOptions<TData> {
  let groupFooter = overrides?.groupTotalRow ?? null;
  let totalFooter = overrides?.grandTotalRow ?? 'bottom';

  const toolPanels: (string | ToolPanelDef)[] = ['columns', 'filters'];

  if (buildOptions.views && !buildOptions.views.hide) {
    toolPanels.push({
      id: 'views',
      labelDefault: 'Visões',
      labelKey: 'viewsLabel',
      iconKey: 'menu',
      toolPanel: AgViewsToolpanelComponent,
      toolPanelParams: {
        columns: buildOptions.columnDefs,
        ...buildOptions.views,
      },
    });
  }

  const onFirstDataRenderedOverride = overrides?.onFirstDataRendered;
  if (onFirstDataRenderedOverride) {
    delete overrides.onFirstDataRendered;
  }

  const onColumnRowGroupChangedOverride = overrides?.onColumnRowGroupChanged;
  let columnRowGroupWidths: { key: string; newWidth: number }[] = [];
  if (onColumnRowGroupChangedOverride) {
    delete overrides.onColumnRowGroupChanged;
  }

  const gridOptions: GridOptions<TData> = {
    // Column Definintions
    columnDefs: buildOptions.columnDefs,
    columnTypes: buildColumnTypes(buildOptions.pipes),
    defaultColDef: buildDefaultColumnDef(),
    autoGroupColumnDef: buildAutoGroupColumnDef(),

    // Groups (row)
    groupDisplayType: 'multipleColumns',
    groupHideOpenParents: true, // single row for multiple group columns
    groupDefaultExpanded: -1, // all expanded
    grandTotalRow: 'bottom', // bottom total
    suppressAggFuncInHeader: true, // ex: sum(Header Name) -> Header Name
    rowGroupPanelShow: 'onlyWhenGrouping', // top panel

    // Pivot (column)
    pivotMode: buildOptions.pivotMode,
    pivotRowTotals: 'after', // right total
    pivotDefaultExpanded: -1,
    enableStrictPivotColumnOrder: true,
    processPivotResultColDef: colDef => {
      if (isPivotTotalCell(colDef)) {
        colDef.headerClass = 'pivot-total-header';
      }
    },

    // Other
    headerHeight: 45, // double line header
    cellSelection: true, // excel-like selection
    enableCharts: true, // context menu charts
    cacheQuickFilter: true,
    sideBar: {
      toolPanels,
    },
    statusBar: {
      statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
        // { statusPanel: 'agTotalRowCountComponent', align: 'center' },
        // { statusPanel: 'agFilteredRowCountComponent', align: 'center' },
        { statusPanel: 'agSelectedRowCountComponent' },
        { statusPanel: 'agAggregationComponent' },
      ],
    },

    // Custom Export from Context Menu
    getContextMenuItems: () => {
      return [
        'copy',
        'copyWithHeaders',
        'copyWithGroupHeaders',
        'separator',
        {
          name: 'Limpar Filtros',
          icon: '<span class="ag-icon ag-icon-filter"></span>',
          action: params => params.api.setFilterModel(null),
        },
        'expandAll',
        'contractAll',
        'autoSizeAll',
        'resetColumns',
        'separator',
        {
          name: 'Exibir/Esconder Sub-Total por Grupo',
          icon: '<span class="ag-icon ag-icon-aggregation"></span>',
          action: params => {
            groupFooter = !groupFooter ? 'bottom' : null;
            params.api.updateGridOptions({ groupTotalRow: groupFooter });
          },
        },
        {
          name: 'Exibir/Esconder Grand-Total',
          icon: '<span class="ag-icon ag-icon-aggregation"></span>',
          action: params => {
            totalFooter = !totalFooter ? 'bottom' : null;
            params.api.updateGridOptions({ grandTotalRow: totalFooter });
          },
        },
        'separator',
        'chartRange',
        'pivotChart',
        {
          name: 'Exportar',
          icon: '<span class="ag-icon ag-icon-save"></span>',
          subMenu: [
            'csvExport',
            !buildOptions.exportExcelFn
              ? 'excelExport'
              : {
                  name: 'Exportar para Excel (com filtros)',
                  icon: '<span class="ag-icon ag-icon-excel"></span>',
                  action: () => buildOptions.exportExcelFn(),
                },
          ],
        },
      ];
    },

    // Events
    onFirstDataRendered: event => {
      // console.log('[Grid] onFirstDataRendered');

      if (onFirstDataRenderedOverride) {
        onFirstDataRenderedOverride(event);
      }
    },

    onColumnRowGroupChanged: event => {
      // console.log('[Grid] onColumnRowGroupChanged', event);

      if (onColumnRowGroupChangedOverride) {
        onColumnRowGroupChangedOverride(event);
      }
    },

    onModelUpdated: event => {
      // console.log('[Grid] onModelUpdated', event);

      const state = event.api.getColumnState();
      // console.log('...state', state);

      const groupCols = state.filter(col => col.colId.startsWith('ag-Grid-AutoColumn-'));
      if (groupCols.find(col => col.width != DEFAULT_GROUP_COL_WIDTH)) {
        columnRowGroupWidths = state.map(col => ({ key: col.colId, newWidth: col.width }));
        // console.log('SAVE WIDTHS:', columnRowGroupWidths);
      } else {
        // console.log('RESTORE WIDTHS:', columnRowGroupWidths);
        event.api.setColumnWidths(columnRowGroupWidths);
      }
    },

    // Localisation
    localeText: AG_GRID_LOCALE_PT_BR,

    // Excel styles
    excelStyles: [],

    // Config overrides
    ...overrides,
  };

  // Reinforce chart data type, after overrides: telas que substituem o autoGroupColumnDef
  // inteiro tambem precisam da coluna de grupo marcada como categorica (ver buildAutoGroupColumnDef).
  gridOptions.autoGroupColumnDef = { chartDataType: 'category', ...gridOptions.autoGroupColumnDef };

  // Reinforce, after overrides: do ag-grid 33 em diante so o `hideOverlay()` desliga o overlay
  // pedido na mao, entao e preciso esconde-lo quando as linhas chegam. Ver ag-grid.factory.spec.ts.
  const onRowDataUpdatedOverride = gridOptions.onRowDataUpdated;
  gridOptions.onRowDataUpdated = event => {
    if (event.api.getDisplayedRowCount() > 0) {
      event.api.hideOverlay();
    }
    onRowDataUpdatedOverride?.(event);
  };

  // Add fixed styles for excel, after overrides
  gridOptions.excelStyles.push({
    id: 'pivot-total-col',
    interior: {
      pattern: 'Solid',
      color: '#eeeeee',
    },
  });
  gridOptions.excelStyles.push({
    id: 'footer-total-col',
    interior: {
      pattern: 'Solid',
      color: '#eeeeee',
    },
  });
  gridOptions.excelStyles.push({
    id: 'int',
    dataType: 'Number',
    numberFormat: {
      format: '0',
    },
  });
  gridOptions.excelStyles.push({
    id: 'money',
    dataType: 'Number',
    numberFormat: {
      format: '#,##0.00',
    },
  });
  gridOptions.excelStyles.push({
    id: 'float',
    dataType: 'Number',
    numberFormat: {
      format: '0.0',
    },
  });
  gridOptions.excelStyles.push({
    id: 'float1',
    dataType: 'Number',
    numberFormat: {
      format: '0.0',
    },
  });
  gridOptions.excelStyles.push({
    id: 'float4',
    dataType: 'Number',
    numberFormat: {
      format: '0.0000',
    },
  });
  gridOptions.excelStyles.push({
    id: 'date',
    dataType: 'DateTime',
    numberFormat: {
      format: 'dd/mm/yyyy',
    },
  });
  gridOptions.excelStyles.push({
    id: 'datetime',
    dataType: 'DateTime',
    numberFormat: {
      format: 'dd/mm/yyyy HH:mm:ss',
    },
  });
  gridOptions.excelStyles.push({
    id: 'str',
    dataType: 'String',
  });
  gridOptions.excelStyles.push({
    id: 'competencia',
    dataType: 'Number',
    numberFormat: {
      format: '0000-00',
    },
  });
  gridOptions.excelStyles.push({
    id: 'cpfCnpj',
    dataType: 'String',
  });
  gridOptions.excelStyles.push({
    id: 'cpf',
    dataType: 'String',
    /*
    dataType: 'Number',
    numberFormat: {
      format: '000\\.000\\.000-00',
    },
    */
  });
  gridOptions.excelStyles.push({
    id: 'cnpj',
    dataType: 'String',
    /*
    dataType: 'Number',
    numberFormat: {
      format: `00\\.000\\.000\\/0000-00`,
    },
    */
  });

  return gridOptions;
}

// Clear these properties
export const CLEAR_VIEW: ColDef = {
  pivotIndex: null,
  rowGroupIndex: null,
  aggFunc: null,
  hide: null,
};

/**
 * Opção de configuração das visões.
 */
export interface ViewOptions {
  pivots: string[];
  linhas: string[];
  metricas: string[] | Record<string, AGG_FUNC>;
}

/**
 * Constrói definições de colunas, partindo das definições base,
 * conforme as configurações de visões especificadas.
 *
 * @param baseColDef definições base das colunas.
 * @param options configurações da visão.
 * @returns definições de colunas de acordo com as configurações da visão.
 */
export function buildViewColDefs(baseColDef: ColDef[], options?: ViewOptions): ColDef[] {
  if (!options) {
    return baseColDef.map(col => ({
      ...CLEAR_VIEW,
      ...col,
    }));
  }

  const { pivots, linhas, metricas } = options;

  let metricasFields: string[];
  let metricasMap: Record<string, AGG_FUNC>;
  if (Array.isArray(metricas)) {
    metricasFields = metricas;
    metricasMap = metricas.reduce((acc, metrica) => {
      acc[metrica] = 'sum';
      return acc;
    }, {});
  } else {
    metricasFields = Object.keys(metricas);
    metricasMap = metricas;
  }

  const visible = [].concat(pivots, linhas, metricasFields);

  // TODO metric order?

  return baseColDef.map(col => ({
    ...CLEAR_VIEW,
    ...col,
    pivotIndex: pivots.includes(col.field) ? pivots.indexOf(col.field) : null,
    rowGroupIndex: linhas.includes(col.field) ? linhas.indexOf(col.field) : null,
    aggFunc: col.field in metricasMap ? metricasMap[col.field] : null,
    hide: visible.includes(col.field) ? null : true,
  }));
}

export function updateSidebarViews(grid: AgGridAngular, views: AgViewsToolpanelParams) {
  const toolPanels: (string | ToolPanelDef)[] = ['columns', 'filters'];

  if (views && !views.hide) {
    toolPanels.push({
      id: 'views',
      labelDefault: 'Visões',
      labelKey: 'viewsLabel',
      iconKey: 'menu',
      toolPanel: AgViewsToolpanelComponent,
      toolPanelParams: {
        columns: grid.columnDefs,
        ...views,
      },
    });
  }

  grid.api.setGridOption('sideBar', {
    toolPanels,
  });
}

//*** Funções de Parsing ***//

function parseNumber(params: ValueParserParams): number | null {
  console.log('parseNumber', params.newValue);
  if (!params.newValue) {
    console.log('parseNumber: null');
    return null;
  }

  const n = parseFloat(params.newValue.replaceAll(',', '.'));
  console.log('parseNumber:', n);

  return n;
}

function parseDate(dateString: string, ignoreTime = false): Date | null {
  console.log('parseDate', dateString);
  if (!dateString) {
    console.log('parseDate: null');
    return null;
  }

  const d = new Date(dateString);
  if (isNaN(d.getTime())) {
    console.log('parseDate:', d);
    return null;
  }

  if (ignoreTime) {
    d.setHours(0, 0, 0, 0);
  }

  console.log('parseDate:', d);
  return d;
}

//*** Funções de Exportação ***//

export interface ExportOptions {
  api: GridApi;
  headers: string[];
  extraFilters?: Record<string, string>;
  fileNamePrefix: string;
}

export function exportGridAsExcel(options: ExportOptions, overrides?: ExcelExportParams) {
  const { api, fileNamePrefix } = options;

  // Always add header
  const headers = [...options.headers, 'Exportado pelo Gestor IDSS em: ' + new Date().toLocaleString('pt-BR')];

  // Handle formatting for typed cells
  const processCellCallbackOverride = overrides?.processCellCallback;
  if (processCellCallbackOverride) {
    delete overrides.processCellCallback;
  }
  const processCellCallback = (params: ProcessCellForExportParams) => {
    let value = params.value;
    if (processCellCallbackOverride) {
      value = processCellCallbackOverride(params);
    }

    const type = params.column.getColDef().type;
    const types = typeof type == 'string' ? [type] : type;

    if (types?.find(type => ['cpf', 'cnpj', 'cpfCnpj'].includes(type))) {
      return CpfCnpjPipe.transform(value);
    }

    if (types?.find(type => ['date', 'datetime'].includes(type))) {
      if (params.value) {
        return new Date(params.value).toISOString().substring(0, 23);
      } else {
        return null;
      }
    }

    return value;
  };

  const totalRows = api.getDisplayedRowCount();
  const totalParts = Math.ceil(totalRows / EXPORT_MAX_SIZE);

  // Somente uma parte, exportação simples
  if (totalParts <= 1) {
    const fileName = `${fileNamePrefix}-em-${isoDateNoPuncts()}.xlsx`;

    api.exportDataAsExcel({
      prependContent: AgFilterUtils.buildFilterRows(api, headers, options.extraFilters),
      fileName,
      processCellCallback,
      ...overrides,
      ...EXPORT_COPYRIGHT,
    });

    return;
  }

  // Quebra em multiplas partes

  const originalHeader = headers[0];

  for (let i = 0; i < totalParts; i++) {
    headers[0] = `${originalHeader} - ${i + 1} de ${totalParts}`;
    const fileName = `${fileNamePrefix}-em-${isoDateNoPuncts()}-${i + 1}.xlsx`;

    api.exportDataAsExcel({
      prependContent: AgFilterUtils.buildFilterRows(api, headers, options.extraFilters),
      fileName,
      processCellCallback,
      shouldRowBeSkipped: params => {
        return !(params.node.rowIndex >= i * EXPORT_MAX_SIZE && params.node.rowIndex < (i + 1) * EXPORT_MAX_SIZE);
      },
      ...overrides,
      ...EXPORT_COPYRIGHT,
    });
  }
}
